import { z } from 'zod'

import { email, password, role } from './common.js'

/**
 * 인증 API 계약.
 *
 * 백엔드는 이 스키마로 요청을 검증하고 응답을 직렬화한다.
 * 프론트는 같은 타입을 그대로 import한다. 한쪽이 어기면 타입 에러가 즉시 난다.
 *
 * 근거: 설계 명세서 1장
 */

// ── 로그인 ──────────────────────────────────────────────────

export const loginRequest = z.object({
  email,
  password: z.string().min(1, '비밀번호를 입력하세요'), // 로그인은 정책 검증 안 함. 틀리면 그냥 실패
})
export type LoginRequest = z.infer<typeof loginRequest>

/**
 * 로그인 응답에는 현재 사용자 정보만 담는다.
 *
 * 세션 토큰은 본문이 아니라 httpOnly 쿠키로 나간다(설계 명세서 1.4).
 * 본문에 토큰을 실으면 JS가 읽을 수 있게 되어 httpOnly의 의미가 사라진다.
 */
export const sessionUser = z.object({
  id: z.number().int(),
  email: z.string(),
  name: z.string(),
  role,
  /** 기업 소속이면 기업 id, 업체 소속이면 업체 id. 운영자는 둘 다 null */
  companyId: z.number().int().nullable(),
  vendorId: z.number().int().nullable(),
})
export type SessionUser = z.infer<typeof sessionUser>

export const loginResponse = z.object({ user: sessionUser })
export type LoginResponse = z.infer<typeof loginResponse>

// ── 회원가입 (셀프, BIZ-67) ─────────────────────────────────
//
// 초대 없이 스스로 가입한다. company를 "바이어 계정"으로 재사용하며 subjectType으로 구분한다.
// 개인은 orgName=본인 성명. 기업만 사업자등록번호 필수(가승인·검증은 BIZ-68).

/** 계정 주체 유형(스펙 SUBJECT_TYPES와 동일 값). */
export const subjectType = z.enum(['COMPANY', 'ORG', 'INDIVIDUAL', 'GROUP', 'ETC'])
export type SubjectType = z.infer<typeof subjectType>

const optSignupMobile = z.preprocess(
  (v) => (v === '' || v == null ? undefined : v),
  z
    .string()
    .transform((v) => v.replace(/[^0-9]/g, ''))
    .refine((v) => /^01[016789]\d{7,8}$/.test(v), '휴대폰 번호를 확인하세요')
    .optional(),
)
const optSignupBizRegNo = z.preprocess(
  (v) => (v === '' || v == null ? undefined : v),
  z
    .string()
    .transform((v) => v.replace(/[^0-9]/g, ''))
    .refine((v) => v.length === 10, '사업자등록번호는 10자리입니다')
    .optional(),
)

export const signupRequest = z
  .object({
    subjectType,
    /** 바이어 계정명(company.name). 개인이면 본인 성명. */
    orgName: z.string().min(1, '이름을 입력하세요').max(100),
    /** 로그인 사용자 성명(app_user.name). 생략하면 orgName을 쓴다(개인용). */
    contactName: z.string().min(1).max(50).optional(),
    email,
    password,
    phone: optSignupMobile,
    bizRegNo: optSignupBizRegNo,
    /** 대표자명(기업, 국세청 진위확인용 BIZ-120). company.ceo_name. */
    ceoName: z.string().min(1).max(50).optional(),
    /** 개업일자 YYYY-MM-DD(기업, 진위확인용). */
    bizOpenDate: z.string().date().optional(),
    /** 추천 코드(BIZ-75). 링크의 ?ref=. 빈 값·공백은 없는 것으로 본다. 검증은 서버가 하고, 틀려도 가입은 막지 않는다. */
    referralCode: z.preprocess(
      (v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined),
      z.string().max(32).optional(),
    ),
  })
  // 기업은 사업자등록번호가 필수(가승인·검증의 근거). 비기업은 없어도 된다.
  .refine((d) => d.subjectType !== 'COMPANY' || !!d.bizRegNo, {
    message: '사업자등록번호를 입력하세요',
    path: ['bizRegNo'],
  })
  // 기업은 국세청 진위확인을 위해 대표자명·개업일도 받는다(BIZ-120).
  .refine((d) => d.subjectType !== 'COMPANY' || !!d.ceoName, {
    message: '대표자명을 입력하세요',
    path: ['ceoName'],
  })
  .refine((d) => d.subjectType !== 'COMPANY' || !!d.bizOpenDate, {
    message: '개업일자를 입력하세요',
    path: ['bizOpenDate'],
  })
export type SignupRequest = z.infer<typeof signupRequest>

export const signupResponse = z.object({ user: sessionUser })
export type SignupResponse = z.infer<typeof signupResponse>

// ── 현재 사용자 조회 ────────────────────────────────────────

export const meResponse = z.object({ user: sessionUser })
export type MeResponse = z.infer<typeof meResponse>

// ── 초대 수락 ──────────────────────────────────────────────

/**
 * 초대 링크를 연 사람이 비밀번호를 설정한다. 이 시점에 계정이 ACTIVE가 된다.
 * 운영자가 임시 비밀번호를 만들어 전달하는 경로는 없다(설계 명세서 1.2).
 */
export const acceptInviteRequest = z.object({
  token: z.string().min(1),
  password, // 여기서는 정책을 강제한다. 최초 설정이므로.
})
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequest>

export const acceptInviteResponse = z.object({ user: sessionUser })
export type AcceptInviteResponse = z.infer<typeof acceptInviteResponse>

/**
 * 초대 링크를 열었을 때, 비밀번호 입력 전에 보여줄 정보.
 * 누가 어떤 자격으로 초대됐는지 확인시켜 준다.
 * 토큰이 유효하지 않으면 이 조회 자체가 실패한다.
 */
export const inviteInfoResponse = z.object({
  email: z.string(),
  name: z.string(),
  role,
  organizationName: z.string(), // 기업명 또는 업체명. 운영자 초대면 '포비즈 운영팀'
})
export type InviteInfoResponse = z.infer<typeof inviteInfoResponse>

// ── 비밀번호 재설정 (BIZ-55) ────────────────────────────────

/**
 * 재설정 요청. 이메일만 받는다. 계정 존재 여부를 응답에서 드러내지 않으려
 * 서버는 **항상 성공(200)** 을 돌려준다(이메일 열거 방지). 링크는 알림톡으로 발송.
 */
export const passwordResetRequestSchema = z.object({ email })
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>

/** 재설정 링크를 열고 새 비밀번호를 설정한다. 최초 설정처럼 정책을 강제한다. */
export const passwordResetConfirmSchema = z.object({
  token: z.string().min(1),
  password,
})
export type PasswordResetConfirmRequest = z.infer<typeof passwordResetConfirmSchema>

/** 재설정 토큰이 아직 유효한지(비밀번호 입력 화면 진입 전 확인). 유효하면 어느 계정인지
 * 마스킹 이메일(de***@forbiz.kr)로 알려줘 사용자를 안심시킨다 — 전체 이메일은 노출하지 않는다. */
export const passwordResetInfoResponse = z.object({
  valid: z.boolean(),
  maskedEmail: z.string().nullable().optional(),
})
export type PasswordResetInfoResponse = z.infer<typeof passwordResetInfoResponse>

// ── 경량 폰 인증 (BIZ-95) — 비밀번호 없는 "번호 확인" ──────────
//
// 휴대폰 번호 확인(알림톡 OTP)만으로 경량 계정을 만들거나 다시 로그인한다.
// "회원가입"이 아니라 "번호 확인" 관점이다(문구·경로 모두). 이메일/비번 계정과 공존하며
// 식별자만 폰 vs 이메일로 갈린다. 세션 정책은 그대로 재사용한다.

/** 휴대폰 번호(필수). 비숫자 제거 후 01x 형식만 통과. */
const mobile = z.preprocess(
  (v) => (typeof v === 'string' ? v.replace(/[^0-9]/g, '') : v),
  z.string().refine((v) => /^01[016789]\d{7,8}$/.test(v), '휴대폰 번호를 확인하세요'),
)

/** 번호 확인 코드(OTP) 발송 요청. */
export const phoneOtpRequestSchema = z.object({ phone: mobile })
export type PhoneOtpRequest = z.infer<typeof phoneOtpRequestSchema>

export const phoneOtpRequestResponse = z.object({ ok: z.literal(true) })
export type PhoneOtpRequestResponse = z.infer<typeof phoneOtpRequestResponse>

/** 번호 확인 코드 검증 → 경량 계정 생성/로그인. */
export const phoneOtpVerifySchema = z.object({
  phone: mobile,
  code: z.string().regex(/^\d{6}$/, '인증번호 6자리를 입력하세요'),
  /** 최초 번호 확인 시 표시명(선택). 없으면 번호를 이름으로 쓴다. */
  name: z.string().min(1).max(50).optional(),
  /** 추천 코드(BIZ-75). 최초 계정 생성 시에만 귀속되고, 틀려도 확인은 진행. */
  referralCode: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined),
    z.string().max(32).optional(),
  ),
})
export type PhoneOtpVerifyRequest = z.infer<typeof phoneOtpVerifySchema>

export const phoneOtpVerifyResponse = z.object({ user: sessionUser })
export type PhoneOtpVerifyResponse = z.infer<typeof phoneOtpVerifyResponse>

// ── 로그아웃 ────────────────────────────────────────────────

export const logoutResponse = z.object({ ok: z.literal(true) })
export type LogoutResponse = z.infer<typeof logoutResponse>

// ── 에러 코드 ──────────────────────────────────────────────
//
// 프론트가 이 코드로 분기한다. 문자열을 하드코딩하지 않게 여기서 모은다.

export const AuthErrorCode = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  ACCOUNT_INACTIVE: 'ACCOUNT_INACTIVE',
  NOT_AUTHENTICATED: 'NOT_AUTHENTICATED',
  INVITE_INVALID: 'INVITE_INVALID',
  INVITE_EXPIRED: 'INVITE_EXPIRED',
  RESET_INVALID: 'RESET_INVALID', // 재설정 토큰이 없거나 이미 사용됨
  RESET_EXPIRED: 'RESET_EXPIRED',
  DUPLICATE_EMAIL: 'DUPLICATE_EMAIL', // 셀프가입 — 이미 가입된 이메일
  DUPLICATE_BIZ_REG_NO: 'DUPLICATE_BIZ_REG_NO', // 셀프가입 — 이미 등록된 사업자등록번호
  OTP_INVALID: 'OTP_INVALID', // 번호 확인 — 코드가 없거나 틀림
  OTP_EXPIRED: 'OTP_EXPIRED', // 번호 확인 — 코드 만료
  OTP_LOCKED: 'OTP_LOCKED', // 번호 확인 — 시도 초과, 재발송 필요
  OTP_THROTTLED: 'OTP_THROTTLED', // 번호 확인 — 발송 요청 과다
  VALIDATION_FAILED: 'VALIDATION_FAILED',
} as const
export type AuthErrorCode = (typeof AuthErrorCode)[keyof typeof AuthErrorCode]
